// Helper functions for determining pitch states and display properties

/**
 * Determine the current state of a pitch
 * @param {Object} pitch - The pitch object
 * @returns {string} The pitch state: 'draft', 'scheduled', 'sent', or 'read'
 */
export function getPitchState(pitch) {
  if (!pitch) return 'unknown';

  if (pitch.draft === true) {
    return 'draft';
  }

  if (pitch.scheduled_for && !pitch.sent_at) {
    const scheduledTime = new Date(pitch.scheduled_for);
    const now = new Date();
    if (scheduledTime > now) {
      return 'scheduled';
    }
  }

  if (pitch.read_at) {
    return 'read';
  }

  if (pitch.sent_at) {
    return 'sent';
  }

  return 'draft';
}

/**
 * Check if a pitch is scheduled for the future
 * @param {Object} pitch - The pitch object
 * @returns {boolean} True if scheduled for future
 */
export function isScheduledForFuture(pitch) {
  if (!pitch || !pitch.scheduled_for) return false;

  const scheduledTime = new Date(pitch.scheduled_for);
  const now = new Date();
  return scheduledTime > now && !pitch.sent_at;
}

/**
 * Check if a pitch has been edited after sending
 * @param {Object} pitch - The pitch object
 * @returns {boolean} True if edited after sending
 */
export function isEditedAfterSend(pitch) {
  return !!(pitch && pitch.after_send_edited_at);
}

/**
 * Check if a pitch has been read by the recipient
 * @param {Object} pitch - The pitch object
 * @returns {boolean} True if read
 */
export function isPitchRead(pitch) {
  return !!(pitch && pitch.read_at);
}

/**
 * Get the appropriate CSS class for a pitch state
 * @param {string} state - The pitch state
 * @returns {string} Bootstrap CSS class
 */
export function getPitchStateClass(state) {
  switch (state) {
    case 'draft':
      return 'alert-info';
    case 'scheduled':
      return 'border-warning';
    case 'read':
      return 'border-success';
    case 'sent':
      return '';
    default:
      return '';
  }
}

/**
 * Get display text for a pitch state
 * @param {string} state - The pitch state
 * @returns {string} Human-readable state text
 */
export function getPitchStateText(state) {
  switch (state) {
    case 'draft':
      return 'Draft';
    case 'scheduled':
      return 'Scheduled';
    case 'read':
      return 'Read';
    case 'sent':
      return 'Sent';
    default:
      return '';
  }
}

/**
 * Determine if the pitch belongs to the current user's profile
 * @param {Object} pitch - The pitch object
 * @param {string} currentProfileId - The current user's profile ID (not user ID!)
 * @returns {boolean} True if the pitch belongs to the current user's profile
 */
export function isOwnPitch(pitch, currentProfileId) {
  if (!pitch || !currentProfileId) return false;
  return pitch.profile_id === currentProfileId;
}

/**
 * Get the appropriate icon for a pitch state
 * @param {string} state - The pitch state
 * @returns {string} FontAwesome icon class
 */
export function getPitchStateIcon(state) {
  switch (state) {
    case 'draft':
      return 'fa-regular fa-file-lines';
    case 'scheduled':
      return 'fa-regular fa-clock';
    case 'read':
      return 'fa-solid fa-circle-check text-success';
    case 'sent':
      return 'fa-solid fa-paper-plane';
    default:
      return '';
  }
}
